/**
 * Placeholder copy for the two composers.
 *
 * Upstream mounts one composer on both the thread and the `/new` landing, so a
 * user who starts a draft on the landing and keeps typing in the thread reads
 * the same prompt. Forge renders two composers, so the copy lives in one module
 * and both call into it. That is what stops the landing copy drifting away from
 * the thread's again.
 */

/**
 * t3code's copy, verbatim. It advertises the skill trigger as well as the
 * mention and command ones, and Forge parses all three (see composer-logic's
 * "$" branch), so the shorter sentence was under-selling what the editor can
 * already do.
 */
const COMPOSER_DEFAULT_PLACEHOLDER =
  "Ask anything, @tag files/folders, $use skills, or / for commands";

/**
 * t3code's copy for a composer whose thread is not connected. It says what the
 * composer is for rather than how to drive it, because none of the triggers
 * work until the connection is back.
 */
export const DISCONNECTED_COMPOSER_PLACEHOLDER =
  "Ask for changes, send follow-ups, or attach images";

/** Thread composer placeholder, which states whatever the composer is waiting on. */
export function getComposerPromptPlaceholder(input: {
  isComposerApprovalState: boolean;
  activeApprovalDetail: string | null;
  hasActivePendingProgress: boolean;
  showPlanFollowUpPrompt: boolean;
  hasActiveProposedPlan: boolean;
  phase: string;
}): string {
  if (input.isComposerApprovalState) {
    return input.activeApprovalDetail ?? "Resolve this approval request to continue";
  }
  if (input.hasActivePendingProgress) {
    return "Type your own answer, or leave this blank to use the selected option";
  }
  if (input.showPlanFollowUpPrompt && input.hasActiveProposedPlan) {
    return "Add feedback to refine the plan, or leave this blank to implement it";
  }
  if (input.phase === "disconnected") {
    return DISCONNECTED_COMPOSER_PLACEHOLDER;
  }
  return COMPOSER_DEFAULT_PLACEHOLDER;
}

/**
 * Landing composer placeholder. Same string regardless of project state:
 * picking a project happens in the heading above, not via dead-end onboarding
 * copy in the textarea.
 */
export function buildComposerPlaceholder(_projectName: string | null): string {
  return COMPOSER_DEFAULT_PLACEHOLDER;
}
